import { CircularProgress, IconButton, SvgIcon, Tooltip } from '@mui/material';
import { Star as StarIcon, StarOutline as StarOutlineIcon } from '@opengov/react-capital-assets';
import React from 'react';

import { DATA_TEST } from '../enums';

import { NAV_COLOR_PALETTE, tooltipOffsetProps } from './styleConstants';

interface IFavoriteStarButtonProps {
  favorited: boolean;
  styles?: React.CSSProperties;
  labelRef?: React.MutableRefObject<any>;
  starRef?: React.MutableRefObject<any>;
  onClick(e: React.MouseEvent<HTMLButtonElement>): Promise<void>;
}
export const FavoriteStarButton: React.FC<IFavoriteStarButtonProps> = ({
  favorited,
  styles,
  starRef,
  labelRef,
  onClick
}) => {
  const [awaitingOnClickResponse, setAwaitingOnClickResponse] = React.useState(false);
  return (
    <Tooltip
      title={favorited ? 'Remove from Favorites' : 'Add to your Favorites menu'}
      placement="top"
      enterNextDelay={500}
      disableInteractive={true}
      {...tooltipOffsetProps}
    >
      <IconButton
        disableRipple={true}
        data-test={DATA_TEST.favorite_star_button}
        tabIndex={-1}
        sx={{
          padding: 0,
          borderRadius: 0.5,
          marginLeft: 1,
          marginRight: awaitingOnClickResponse ? 0.5 : undefined,
          '&:hover': {
            backgroundColor: 'action.hover'
          },
          '&.Mui-focusVisible, :focus-visible': {
            // Per MUI docs, Disabling ripple on focus removes all styling
            // Ensure focus style exists for accessibility
            outline: (theme: any) => `2px solid ${theme.palette.primary.main}`,
            borderRadius: '50%',
            backgroundColor: 'action.hover'
          },
          ...styles
        }}
        onClick={async (e) => {
          // Prevent API spamming by waiting for the onClick function to complete before calling it again
          if (awaitingOnClickResponse) return;
          setAwaitingOnClickResponse(true);
          await onClick(e);
          setAwaitingOnClickResponse(false);
        }}
        ref={starRef}
        onKeyDown={(e) => {
          if ((e.key === 'ArrowRight' || e.key === 'ArrowLeft') && labelRef?.current) {
            labelRef.current.focus();
          }
        }}
        aria-busy={awaitingOnClickResponse}
      >
        {awaitingOnClickResponse && (
          <CircularProgress
            size={20}
            sx={{ color: 'text.primary' }}
            aria-label="Processing favorite star button click"
          />
        )}
        {!awaitingOnClickResponse && favorited && (
          <SvgIcon
            fontSize="medium"
            sx={{ color: NAV_COLOR_PALETTE.favoriteStar }}
            component={StarIcon}
            inheritViewBox
            data-test={DATA_TEST.favorite_star_button_filled}
          />
        )}
        {!awaitingOnClickResponse && !favorited && (
          <SvgIcon
            fontSize="medium"
            sx={{ color: 'text.primary', fill: 'currentColor' }}
            component={StarOutlineIcon}
            inheritViewBox
            data-test={DATA_TEST.favorite_star_button_outline}
          />
        )}
      </IconButton>
    </Tooltip>
  );
};
